import React, { createContext, useCallback, useContext, useState, useEffect } from 'react';
import { useNavigate, useLocation  } from 'react-router-dom';
import { t } from "./utils/i18n";
import { useFeedback } from "./feedbackContext";

const AuthContext = createContext();
export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [currentUserId, setCurrentUserId] = useState("");
    const [currentUserAlias, setCurrentUserAlias] = useState("");
    const [currentUserEmail, setCurrentUserEmail] = useState("");
    const [loading, setLoading] = useState(true);
    const [authConnectionError, setAuthConnectionError] = useState("");
    const { showFeedback } = useFeedback();
    const navigate = useNavigate();
    const location = useLocation();

    const checkAuth = useCallback(async ({ silent = false } = {}) => {
      if (!silent) {
        setLoading(true);
      }
      try {
        const response = await fetch(process.env.REACT_APP_API_URL+'/check-auth', { credentials: 'include' });
        //console.log("url", process.env.REACT_APP_API_URL+'/check-auth');
        //console.log("Checking authentication status...", response);
        if (response.ok) {
          //console.log("Response is ok", response);
          const data = await response.json();
          //console.log(data);
          //console.log("User is authenticated:", data.isAuthenticated);
          setIsAuthenticated(data.isAuthenticated);
          setCurrentUserId(data.userId || "");
          setCurrentUserAlias(data.alias || data.userId || "");
          setCurrentUserEmail(data.email || "");
          setAuthConnectionError("");
        }else{
          console.log("Either not authenticated or failed to fetch auth status.");
          setIsAuthenticated(false);
          setCurrentUserId("");
          setCurrentUserAlias("");
          setCurrentUserEmail("");
          setAuthConnectionError("");
        }
      } catch (error) {
        console.error("Authentication check failed", error);
        const connectionMessage = t("auth.connection.local_https_error");
        setIsAuthenticated(false); 
        setCurrentUserId("");
        setCurrentUserAlias("");
        setCurrentUserEmail("");
        setAuthConnectionError(connectionMessage);
        if (!silent) {
          showFeedback({
            module: "app",
            tone: "error",
            message: connectionMessage,
          });
        }
      }finally {
        setLoading(false); // Set loading to false after check
      }
    }, [showFeedback]);
  
    useEffect(() => {
      checkAuth();
      const interval = setInterval(() => checkAuth({ silent: true }), 10 * 60 * 1000);
      return () => clearInterval(interval);
    }, [checkAuth]);

    useEffect(() => {
      const protectedPaths = ["/grimoire", "/sigil", "/chat"];
      const isProtectedPath = protectedPaths.some((path) =>
        location.pathname === path || location.pathname.startsWith(`${path}/`)
      );

      if (!loading && !isAuthenticated && !authConnectionError && isProtectedPath) {
        console.log("User is not authenticated. Navigating to home...");
        navigate("/");
      }
     }, [authConnectionError, isAuthenticated, loading, navigate, location.pathname]);

    // Public pages (/, /privacy, /terms) render immediately; protected routes
    // wait on `authLoading` inside PrivateRoute instead of blocking the whole app.
    return (
      <AuthContext.Provider value={{ isAuthenticated, setIsAuthenticated, currentUserId, setCurrentUserId, currentUserAlias, setCurrentUserAlias, currentUserEmail, setCurrentUserEmail, refreshAuth: checkAuth, authLoading: loading, authConnectionError }}>
        {children}
      </AuthContext.Provider>
    );
  };
